import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { useClaims } from "../../../modules/generic/hooks/use-claims";
import { withSupabaseToken } from "./utils";

import type { Database } from "../../../__definitions__/database-definitions";
import type { SupabaseHandlerArg, SupabaseSchemaName } from "./types";
import type { DependencyList } from "react";
/**
 * An Effect for loading data from Supabase; may redirect if not authenticated.
 */
export function useSupabase<SchemaName extends SupabaseSchemaName<Database> = "public">(
  callback: (arg0: SupabaseHandlerArg<SchemaName>) => Promise<void>,
  deps: DependencyList,
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  schema: SchemaName = "public" as SchemaName,
): void {
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const claims = useClaims();

  /* oxlint-disable react-hooks/exhaustive-deps -- Callers list their callback's dependencies in `deps`; listing the callback itself would re-run this on every render */
  useEffect(() => {
    const abortController = new AbortController();
    void withSupabaseToken<SchemaName>(
      claims,
      isLoading,
      isAuthenticated,
      getAccessTokenSilently,
      loginWithRedirect,
      schema,
      abortController,
    )
      // oxlint-disable-next-line promise/no-callback-in-promise
      .then((ctx) => (ctx ? callback({ ...ctx, abortController }) : null))
      .catch(console.error);
    return () => {
      abortController?.abort();
    };
  }, [
    claims,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    schema,
    ...deps,
  ]);
  /* oxlint-enable react-hooks/exhaustive-deps */
}
